import { ParticipantProfile } from "../../../models/entities/participant.model";
import { useEffect, useState } from "react";
import { Form } from "react-final-form";
import ParticipantDetailsCard from "./ParticipantDetailsCard";
import { EndpointsService } from "../../../services/endpoints/endpoints.service";
import { FormApi } from "final-form";
import { PtDetailsMode } from "../../../models/pages/participant-details/participant-details.model";
import UiButton from "../../../components/button/Button";
import UiInputText from "../../../components/input-text/InputText.component";
import { Validations } from "../../../services/form/validations.service";
import ParticipantDetailsField from "./ParticipantDetailsField";
import { TbTrash } from "react-icons/tb";
import { Formatter } from "../../../services/formatter/formatter.service";
import UiSelect from "../../../components/select/Select.component";
import { ListOption } from "../../../models/misc.model";
import FormUiInputSwitch from "../../../components/input-switch/InputSwitch.component";
import { useTranslation } from "react-i18next";
import { ParticipantContextProps } from "../../../models/contexts/participant-context.model";
import {
	PtCaregiverInfo,
	PtCaregiversInfoForm,
} from "../../../models/pages/participant-details/caregivers-info.model";
import { CaregiversUpdateBody } from "../../../models/endpoints/caregivers-endpoint.model";
import {
	Relationship,
	RelationshipKey,
	normalizeRelationship,
} from "../../../models/entities/caregiver.model";

const ParticipantDetailsCaregivers = ({
	participant,
	participantContext,
}: {
	participant: ParticipantProfile;
	participantContext: ParticipantContextProps;
}) => {
	const { t } = useTranslation("common");
	const [mode, setMode] = useState<PtDetailsMode>(PtDetailsMode.READ);
	const [showError, setShowError] = useState<boolean>(false);
	const [formData, setFormData] = useState<PtCaregiversInfoForm>({
		data: [],
	});

	useEffect(() => {
		setFormData({
			data: participant.caregivers
				? participant.caregivers.map((x) => new PtCaregiverInfo(x))
				: [],
		});
	}, [participant.caregivers]);

	const add = () => {
		const data = [...formData.data];
		data.push(
			new PtCaregiverInfo({
				patientId: participant.id,
				deleted: false,
			})
		);
		setFormData({ data: [...data] });
	};

	const remove = (id: number | null, index: number) => {
		if (id !== null) {
			setFormData({
				data: formData.data.map((x) => {
					if (id === x.id) {
						x.deleted = true;
					}
					return x;
				}),
			});
		} else {
			const data = [...formData.data];
			data.splice(index, 1);
			setFormData({
				data: [...data],
			});
		}
	};

	const hasChanged = (formItem: PtCaregiverInfo) => {
		let result = false;

		// Find
		if (formItem.id && participant.caregivers) {
			const ph = participant.caregivers.find((x) => x.id === formItem.id);
			if (
				ph &&
				(ph.name !== formItem.name ||
					ph.email !== formItem.email ||
					ph.phone !== formItem.phone ||
					normalizeRelationship(ph.relationship) !== formItem.relationship ||
					ph.purposeOfInvolvement !== formItem.purposeOfInvolvement ||
					ph.sendHealthSummaryReport !== formItem.sendHealthSummaryReport)
			) {
				result = true;
			}
		}

		return result;
	};

	const setBody = (): CaregiversUpdateBody => {
		const result: CaregiversUpdateBody = [];
		formData.data.forEach((x) => {
			if (x.deleted || !x.id || hasChanged(x)) {
				result.push(x);
			}
		});
		return result;
	};

	const handleSubmit = async (
		data: PtCaregiversInfoForm,
		form: FormApi<PtCaregiversInfoForm, Partial<PtCaregiversInfoForm>>
	) => {
		setShowError(false);
		await EndpointsService.dataRetriever
			.updateCaregivers({
				participantId: participant.id,
				body: setBody(),
			})
			.then(() => {
				form.reset();
				participantContext.onRefresh();
				// Update form mode
				setMode(PtDetailsMode.READ);
			})
			.catch(() => setShowError(true));
	};

	return (
		<Form
			onSubmit={handleSubmit}
			initialValues={formData}
			render={({ handleSubmit, form, submitting }) => (
				<form onSubmit={handleSubmit}>
					<ParticipantDetailsCard
						title="PARTICIPANT.DETAILS.CAREGIVERS.TITLE"
						submitting={submitting}
						mode={mode}
						enableEditing={true}
						showError={showError}
						enableSubmit={form.getState().valid}
						onCancel={() => {
							form.reset();
							setShowError(false);
							setFormData(new PtCaregiversInfoForm(participant.caregivers || []));
							setMode(PtDetailsMode.READ);
						}}
						onModeChange={() => setMode(PtDetailsMode.UPDATE)}
					>
						{/* DATA */}
						{formData.data.length > 0 ? (
							<>
								{formData.data
									// .filter((x) => !x.deleted) // NOT WORKING (WHY??)
									.map((x, index) => {
										return !x.deleted ? (
											<div
												id={index.toString()}
												key={index.toString()}
											>
												<div className="row align-items-center">
													{/* Form fields */}
													<div className="col-10 col-sm-11 col-md-11 col-lg-11">
														<div className="row">
															{/* Name */}
															<div className="col-12 col-sm-6 col-md-4 col-lg-4">
																{mode === PtDetailsMode.READ ? (
																	<ParticipantDetailsField
																		title="PARTICIPANT.DETAILS.CAREGIVERS.FORM_FIELDS.name"
																		value={x.name}
																	/>
																) : (
																	<UiInputText
																		id={`data[${index}].name`}
																		name={`data[${index}].name`}
																		label="PARTICIPANT.DETAILS.CAREGIVERS.FORM_FIELDS.name"
																		onChange={(e) => {
																			const data = [
																				...formData.data,
																			];
																			data[index].name =
																				e.target.value;
																			setFormData({
																				data,
																			});
																		}}
																		validations={[
																			Validations.required,
																		]}
																		disabled={submitting}
																	/>
																)}
															</div>

															{/* Email */}
															<div className="col-12 col-sm-6 col-md-5 col-lg-5">
																{mode === PtDetailsMode.READ ? (
																	<ParticipantDetailsField
																		title="PARTICIPANT.DETAILS.CAREGIVERS.FORM_FIELDS.email"
																		value={x.email}
																	/>
																) : (
																	<UiInputText
																		id={`data[${index}].email`}
																		name={`data[${index}].email`}
																		label="PARTICIPANT.DETAILS.CAREGIVERS.FORM_FIELDS.email"
																		onChange={(e) => {
																			const data = [
																				...formData.data,
																			];
																			data[index].email =
																				e.target.value;
																			setFormData({
																				data,
																			});
																		}}
																		validations={[]}
																		disabled={submitting}
																	/>
																)}
															</div>

															{/* Phone */}
															<div className="col-12 col-sm-6 col-md-3 col-lg-3">
																{mode === PtDetailsMode.READ ? (
																	<ParticipantDetailsField
																		title="PARTICIPANT.DETAILS.CAREGIVERS.FORM_FIELDS.phone"
																		value={
																			x.phone
																				? Formatter.phoneNumber(
																						x.phone
																					)
																				: Formatter.NO_DATA
																		}
																	/>
																) : (
																	<UiInputText
																		id={`data[${index}].phone`}
																		name={`data[${index}].phone`}
																		label="PARTICIPANT.DETAILS.CAREGIVERS.FORM_FIELDS.phone"
																		onChange={(e) => {
																			const data = [
																				...formData.data,
																			];
																			data[index].phone =
																				e.target.value;
																			setFormData({
																				data,
																			});
																		}}
																		validations={
																			formData.data[index]
																				.phone
																				? [
																						Validations.regex(
																							/^\d{10}$/,
																							"PARTICIPANT.DETAILS.CAREGIVERS.MSG_VALIDATION_PHONE"
																						),
																					]
																				: []
																		}
																		disabled={submitting}
																	/>
																)}
															</div>

															{/* Relationship */}
															<div className="col-12 col-sm-8 col-md-4 col-lg-4">
																{mode === PtDetailsMode.READ ? (
																	<ParticipantDetailsField
																		title="PARTICIPANT.DETAILS.CAREGIVERS.FORM_FIELDS.relationship"
																		value={
																			x.relationship
																				? t(
																						`ENUMS.CAREGIVER_RELATIONSHIP.${normalizeRelationship(x.relationship)}`,
																						{
																							defaultValue:
																								t(
																									"ENUMS.CAREGIVER_RELATIONSHIP.OTHER"
																								),
																						}
																					)
																				: Formatter.NO_DATA
																		}
																	/>
																) : (
																	<UiSelect
																		id={`data[${index}].relationship`}
																		name={`data[${index}].relationship`}
																		label="PARTICIPANT.DETAILS.CAREGIVERS.FORM_FIELDS.relationship"
																		onChange={(e) => {
																			const data = [
																				...formData.data,
																			];
																			data[
																				index
																			].relationship = e
																				.target
																				.value as RelationshipKey | null;
																			setFormData({
																				data,
																			});
																		}}
																		disabled={submitting}
																		filter={true}
																		showClear={true}
																		options={Object.keys(
																			Relationship
																		).map(
																			(x) =>
																				new ListOption({
																					id: x,
																					label: t(
																						`ENUMS.CAREGIVER_RELATIONSHIP.${x.toUpperCase()}`
																					),
																				})
																		)}
																	/>
																)}
															</div>

															{/* Send Health Summary Report */}
															<div className="col-12 col-sm-6 col-md-6 col-lg-6">
																{mode === PtDetailsMode.READ ? (
																	<ParticipantDetailsField
																		title="PARTICIPANT.DETAILS.CAREGIVERS.FORM_FIELDS.sendHealthSummaryReport"
																		value={
																			x.sendHealthSummaryReport
																		}
																	/>
																) : (
																	<FormUiInputSwitch
																		id={`data[${index}].sendHealthSummaryReport`}
																		name={`data[${index}].sendHealthSummaryReport`}
																		label="PARTICIPANT.DETAILS.CAREGIVERS.FORM_FIELDS.sendHealthSummaryReport"
																		onChange={(e) => {
																			const data = [
																				...formData.data,
																			];
																			data[
																				index
																			].sendHealthSummaryReport =
																				e.target.value;
																			setFormData({
																				data,
																			});
																		}}
																		disabled={submitting}
																	/>
																)}
															</div>
														</div>
													</div>

													{/* Remove form */}
													<div className="col-2 col-sm-1 col-md-1 col-lg-1 text-end">
														<UiButton
															className="p-button-text neutral"
															type="button"
															icon={
																<TbTrash
																	style={{ fontSize: "1.5rem" }}
																/>
															}
															disabled={
																submitting ||
																mode === PtDetailsMode.READ
															}
															onClick={() => remove(x.id, index)}
														/>
													</div>
												</div>

												<hr />
											</div>
										) : (
											<></>
										);
									})}
							</>
						) : (
							<></>
						)}

						<div className="d-flex justify-content-center mt-2">
							<UiButton
								id="addCaregiver"
								type="button"
								className="p-button-sm p-button-rounded"
								label="PARTICIPANT.DETAILS.CAREGIVERS.CREATE"
								icon={"pi pi-plus"}
								onClick={() => add()}
								disabled={submitting || mode === PtDetailsMode.READ}
							/>
						</div>
					</ParticipantDetailsCard>
				</form>
			)}
		/>
	);
};

export default ParticipantDetailsCaregivers;
